import { getExchangeRates } from "deso-protocol";
import { useContext, useState } from "react";

import { waitForTxn } from "@/backend/api-helpers";
import { sendPostDiamonds, sendUserDiamonds } from "@/backend/focus";
import { useToast } from "@/components/ui/use-toast";
import { UserContext } from "@/contexts/user-context";
import { integerToNanos, usdToDeso } from "@/utils/currency";

export enum SEND_DIAMONDS_LOADING_STATE {
  IDLE,
  AUTHORIZING,
  SENDING,
  WAITING_FOR_TX,
}

type SendDiamondsPostArgs = {
  diamondLevel: number;
  postHash: string;
};

type SendDiamondsUserArgs = {
  diamondLevel: number;
  publicKey: string;
};

export type SendDiamondsFunction = {
  (args: SendDiamondsPostArgs): Promise<boolean | void>;
  (args: SendDiamondsUserArgs): Promise<boolean | void>;
};

const DIAMOND_LEVEL_MAP: { [key: number]: number } = {
  1: 0.01,
  2: 0.25,
  3: 1,
  4: 5,
  5: 10,
  6: 25,
  7: 100,
  8: 1000,
  // 9: 10000,
  // 10: 100000,
};

export const useSendDiamonds = () => {
  const { user, authorizeGeneralDerivedKey, currentUserPublicKey } =
    useContext(UserContext);
  const { toast } = useToast();
  const [loadingState, setLoadingState] = useState<SEND_DIAMONDS_LOADING_STATE>(
    SEND_DIAMONDS_LOADING_STATE.IDLE,
  );

  const sendDiamonds: SendDiamondsFunction = async (props) => {
    if (!user) return;

    try {
      setLoadingState(SEND_DIAMONDS_LOADING_STATE.AUTHORIZING);

      const { USDCentsPerDeSoExchangeRate } = await getExchangeRates();
      const diamondValueInDeso = usdToDeso(
        DIAMOND_LEVEL_MAP[props.diamondLevel],
        USDCentsPerDeSoExchangeRate,
      );
      const diamondValueInDesoNanos = integerToNanos(diamondValueInDeso);

      const { success: authSuccess, error } = await authorizeGeneralDerivedKey({
        desoLimit: Math.max(
          // We will set it to 5 Deso minimum, so users can keep spamming small amounts
          // For large amount we always ask for authorization
          5,
          diamondValueInDeso,
        ),
        spendAmountDesoNanos: diamondValueInDesoNanos,
      });

      if (!authSuccess) {
        throw error;
      }

      setLoadingState(SEND_DIAMONDS_LOADING_STATE.SENDING);

      const tx = await (() => {
        switch (true) {
          case "postHash" in props:
            return sendPostDiamonds({
              SenderPublicKey: currentUserPublicKey,
              PostHashHex: props.postHash,
              DiamondLevel: props.diamondLevel,
            });
          case "publicKey" in props:
            return sendUserDiamonds({
              SenderPublicKey: currentUserPublicKey,
              RecipientPublicKey: props.publicKey,
              DiamondLevel: props.diamondLevel,
            });
          default:
            throw new Error("Invalid arguments");
        }
      })();

      setLoadingState(SEND_DIAMONDS_LOADING_STATE.WAITING_FOR_TX);

      await waitForTxn({
        transactionHash: tx.TxnHashHex,
      });

      setLoadingState(SEND_DIAMONDS_LOADING_STATE.IDLE);

      return true;
    } catch (e) {
      toast({
        title: "Error",
        description: `An error occurred while sending diamonds. - ${e}`,
        variant: "destructive",
      });
      setLoadingState(SEND_DIAMONDS_LOADING_STATE.IDLE);
      return false;
    }
  };

  return {
    sendDiamonds,
    loadingState,
    DIAMOND_LEVEL_MAP,
  };
};
